import moment from 'moment';
import { createSelector } from 'reselect';

import {
  currentEnv,
  getWorkspaceId,
  hasOrganisationFeature,
  stringToHslColor,
} from '@/utils';

import { statuses } from '@/config';
import { DATETIME_DISPLAY_FORMAT } from '@/config/consts';

const getPollList = (state) => state.pollList;
const getPollDetail = (state) => state.pollDetail;
const getConfigPollDetail = (state) => state.configPollDetail;
const getResourcePollDetail = (state) => state.resourcePollDetail;
const getResultPollDetail = (state) => state.resultPollDetail;
const getReportPollDetail = (state) => state.reportPollDetail;
const getRouteParams = (state) => state.currentRoute.params;
const getUser = (state) => state.user;
const getWorkspaceList = (state) => state.workspaceList;
const getWorkspaceDetail = (state) => state.workspaceDetail;

export const memberInitials = createSelector(
  [getUser],
  (user) => {
    const getInitials = (first = '', last = '') => `${
      first.substring(0, 1).toUpperCase()
    }${
      last.substring(0, 1).toUpperCase()
    }`;

    const intials = {};

    const arr = user ? user.name.split(' ') : [];
    intials[user?.id] = {
      color: stringToHslColor(`${user?.firstName} ${user?.lastName}`, 50, 70),
      initials: getInitials(...arr),
    };

    return intials;
  },
);

export const activeWorkspace = createSelector(
  [getWorkspaceDetail],
  (workspaces) => workspaces.resources[getWorkspaceId()],
);

export const isPrincipal = createSelector(
  [getWorkspaceList],
  (workspaces) => currentEnv().principalWorkspace in workspaces.resources,
);

export const pollList = createSelector(
  [getPollList],
  (list) => Object.values(list.resources).map((poll) => ({
    ...poll,
    createdDisplay: moment(poll.created).format(DATETIME_DISPLAY_FORMAT),
    lastUpdatedDisplay: moment(poll.lastUpdated).format(DATETIME_DISPLAY_FORMAT),
  })),
);


export const pollInfo = createSelector(
  [getPollDetail, getRouteParams],
  (detail, params) => {
    if (!params.pollId || !(params.pollId in detail.resources)) return {};
    const poll = detail.resources[params.pollId];
    return {
      ...poll,
      createdDisplay: moment(poll.created).format(DATETIME_DISPLAY_FORMAT),
      lastUpdatedDisplay: moment(poll.lastUpdated).format(DATETIME_DISPLAY_FORMAT),
    };
  },
);

export const pollDetails = createSelector(
  [getConfigPollDetail, getRouteParams],
  (config, params) => {
    if (!params.pollId || !(params.pollId in config.resources)) return {};
    return config.resources[params.pollId];
  },
);

export const pollResources = createSelector(
  [getResourcePollDetail, getRouteParams],
  (pollResource, params) => {
    if (!params.pollId || !(params.pollId in pollResource.resources)) return {};
    return pollResource.resources[params.pollId];
  },
);

export const pollResults = createSelector(
  [getResultPollDetail, getRouteParams],
  (results, params) => {
    if (!params.pollId || !(params.pollId in results.resources)) return {};
    return results.resources[params.pollId];
  },
);

export const pollReport = createSelector(
  [getReportPollDetail, getRouteParams],
  (reports, params) => {
    if (!params.pollId || !(params.pollId in reports.resources)) return {};
    return reports.resources[params.pollId];
  },
);

export const pollStatus = createSelector(
  [getPollDetail, getRouteParams],
  (polls, params) => {
    if (!params.pollId || !(params.pollId in polls.resources)) return {};
    const poll = polls.resources[params.pollId];
    if (!poll.status || !(poll.status in statuses)) return {};
    return statuses[poll.status];
  },
);

export const availableNav = createSelector(
  [isPrincipal, pollDetails, activeWorkspace],
  (principal, details, workspace) => {
    const nav = [
      { key: 'setup', display: 'Setup', icon: 'mdi-cog' },
      { key: 'questions', display: 'Questions', icon: 'mdi-format-list-checks' },
    ];

    if (details.questions && Object.keys(details.questions).length) {
      nav.push({ key: 'results', display: 'Results', icon: 'mdi-table' });

      if (principal || hasOrganisationFeature(workspace, ['polls.resources'])) {
        nav.push({ key: 'resources', display: 'Resources', icon: 'mdi-link-box-outline' });
      }
    }

    if (principal || hasOrganisationFeature(workspace, ['polls.reporting'])) {
      nav.push({ key: 'reporting', display: 'Reporting', icon: 'mdi-chart-tree' });
    }

    return nav;
  },
);
